import { useMemo, useState } from 'react';

import type { Product } from '~/types';

// --- Types ---

export type ProductSortOption = 'newest' | 'recentlyUpdated';

export interface UseProductFilteringReturn {
  pinnedProductIds: Set<string>;
  setSortOption: (option: ProductSortOption) => void;
  sortedProducts: Product[];
  sortOption: ProductSortOption;
  togglePin: (id: string) => void;
}

// --- Storage ---

const STORAGE_SORT_KEY = 'products-sort-option';
const STORAGE_PINNED_KEY = 'products-pinned-ids';

const VALID_SORT_OPTIONS = new Set<string>(['newest', 'recentlyUpdated']);

function compareByOption(a: Product, b: Product, option: ProductSortOption): number {
  return getSortTimestamp(b, option) - getSortTimestamp(a, option);
}

function getSortTimestamp(product: Product, option: ProductSortOption): number {
  const iso = option === 'newest' ? product.createdAt : product.lastActivityAt;
  return new Date(iso).getTime();
}

function readPinnedIdsFromStorage(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_PINNED_KEY);
    if (stored === null) return new Set();
    const parsed: unknown = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return new Set(parsed.filter((value): value is string => typeof value === 'string'));
    }
  } catch {
    // Corrupted/legacy value — fall back to no pinned products.
  }
  return new Set();
}

function readSortOptionFromStorage(): ProductSortOption {
  try {
    const stored = localStorage.getItem(STORAGE_SORT_KEY);
    if (stored !== null && VALID_SORT_OPTIONS.has(stored)) {
      return stored as ProductSortOption;
    }
  } catch {
    // Storage inaccessible (private browsing, quota, etc.) — fall through to default.
  }
  return 'recentlyUpdated';
}

// --- Sort ---

function writePinnedIdsToStorage(ids: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_PINNED_KEY, JSON.stringify([...ids]));
  } catch {
    // ignore quota errors
  }
}

function writeSortOptionToStorage(option: ProductSortOption): void {
  try {
    localStorage.setItem(STORAGE_SORT_KEY, option);
  } catch {
    // ignore quota errors
  }
}

// --- Hook ---

/**
 * Sort and pin state for the /products grid. Both are page-local, persisted
 * directly to localStorage — not the global uiStore — mirroring
 * useProjectFiltering's sort/filter state for the sibling Projects page.
 * Pattern: docs/design-patterns/frontend/state-management.md (route-state-by-tier)
 */
export function useProductFiltering(products: Product[]): UseProductFilteringReturn {
  const [sortOption, setSortOptionState] = useState<ProductSortOption>(readSortOptionFromStorage);
  const [pinnedProductIds, setPinnedProductIds] = useState<Set<string>>(readPinnedIdsFromStorage);

  const setSortOption = (option: ProductSortOption): void => {
    setSortOptionState(option);
    writeSortOptionToStorage(option);
  };

  const togglePin = (id: string): void => {
    setPinnedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      writePinnedIdsToStorage(next);
      return next;
    });
  };

  const sortedProducts = useMemo(() => {
    const pinned: Product[] = [];
    const unpinned: Product[] = [];
    for (const product of products) {
      (pinnedProductIds.has(product.id) ? pinned : unpinned).push(product);
    }
    const comparator = (a: Product, b: Product): number => compareByOption(a, b, sortOption);
    return [...pinned.sort(comparator), ...unpinned.sort(comparator)];
  }, [products, pinnedProductIds, sortOption]);

  return { pinnedProductIds, setSortOption, sortedProducts, sortOption, togglePin };
}
